import { useCallback, useEffect, useRef, useState } from 'react';

const THRESHOLD = 70;
const MAX_PULL = 120;
const SCROLL_TOP_EPS = 2;

function getScrollTop() {
  return window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
}

function isScrollableOverflow(el) {
  if (!el || el === document.body || el === document.documentElement) return false;
  const style = window.getComputedStyle(el);
  const overflowY = style.overflowY;
  if (overflowY !== 'auto' && overflowY !== 'scroll' && overflowY !== 'overlay') return false;
  return el.scrollHeight > el.clientHeight + 1;
}

function findNestedScrollParent(target) {
  let el = target instanceof Element ? target : target?.parentElement;
  while (el && el !== document.body && el !== document.documentElement) {
    if (isScrollableOverflow(el)) return el;
    el = el.parentElement;
  }
  return null;
}

function hasOpenDialog() {
  return Boolean(
    document.querySelector('[role="dialog"][data-state="open"], [role="dialog"]:not([aria-hidden="true"])')
  );
}

/**
 * Touch pull-down-to-refresh. Fires onRefresh when released past threshold
 * while the page (and any nested scroll parent under the touch) is at top.
 */
export default function usePullToRefresh({ enabled = true, onRefresh, threshold = THRESHOLD } = {}) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const pullingRef = useRef(false);
  const armedRef = useRef(false);
  const startYRef = useRef(0);
  const startXRef = useRef(0);
  const distanceRef = useRef(0);
  const refreshingRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  const resetPull = useCallback(() => {
    pullingRef.current = false;
    armedRef.current = false;
    distanceRef.current = 0;
    setPullDistance(0);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    const onTouchStart = (e) => {
      if (refreshingRef.current) return;
      if (hasOpenDialog()) return;
      if (getScrollTop() > SCROLL_TOP_EPS) return;

      const nested = findNestedScrollParent(e.target);
      if (nested && nested.scrollTop > SCROLL_TOP_EPS) return;

      const touch = e.touches[0];
      if (!touch) return;
      startYRef.current = touch.clientY;
      startXRef.current = touch.clientX;
      pullingRef.current = true;
      armedRef.current = false;
      distanceRef.current = 0;
    };

    const onTouchMove = (e) => {
      if (!pullingRef.current || refreshingRef.current) return;

      const touch = e.touches[0];
      if (!touch) return;

      const dy = touch.clientY - startYRef.current;
      const dx = Math.abs(touch.clientX - startXRef.current);

      if (!armedRef.current) {
        if (dx > dy && dx > 10) {
          resetPull();
          return;
        }
        if (dy < 8) return;
        if (getScrollTop() > SCROLL_TOP_EPS) {
          resetPull();
          return;
        }
        armedRef.current = true;
      }

      if (dy <= 0) {
        distanceRef.current = 0;
        setPullDistance(0);
        return;
      }

      // Resist after threshold so the indicator eases toward MAX_PULL
      const resisted = Math.min(MAX_PULL, dy * 0.55);
      distanceRef.current = resisted;
      setPullDistance(resisted);

      if (resisted > 8) {
        e.preventDefault();
      }
    };

    const onTouchEnd = async () => {
      if (!pullingRef.current) return;
      const distance = distanceRef.current;
      const shouldRefresh = armedRef.current && distance >= threshold && !refreshingRef.current;
      pullingRef.current = false;
      armedRef.current = false;

      if (!shouldRefresh) {
        distanceRef.current = 0;
        setPullDistance(0);
        return;
      }

      refreshingRef.current = true;
      setRefreshing(true);
      setPullDistance(threshold);

      try {
        await Promise.resolve(onRefreshRef.current?.());
      } finally {
        refreshingRef.current = false;
        setRefreshing(false);
        distanceRef.current = 0;
        setPullDistance(0);
      }
    };

    const onTouchCancel = () => {
      if (refreshingRef.current) return;
      resetPull();
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    document.addEventListener('touchcancel', onTouchCancel, { passive: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [enabled, threshold, resetPull]);

  return { pullDistance, refreshing, threshold };
}
